/**
 * "Check the site" on the job page (trust fix, 2026-10-29).
 *
 * Calm amber, never red, and nothing in the bell: intake wasn't sure which
 * store this work order is for, so the job can't go to a tech until someone
 * looks. Two lines side by side: what the paper says, where the job is.
 * "Site is right" lets it be dispatched; "Check the work order" opens the
 * intake review, where the site can be fixed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { confirmSite, siteCheckReviewHref, type SiteCheck } from "@/lib/site-check";

export function SiteCheckPick({
    jobId,
    needed,
    canConfirm,
}: {
    jobId: string;
    /** jobs header `site_check_needed`: only then is the pick fetched. */
    needed: boolean;
    /** jobs.manage — the same permission the server checks. */
    canConfirm: boolean;
}) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["job", jobId, "site-check"],
        queryFn: () => api.get<{ check: SiteCheck | null }>(`/api/jobs/${jobId}/site-check`),
        enabled: needed,
        staleTime: 30_000,
    });
    const confirm = useMutation({
        mutationFn: () => confirmSite(jobId),
        onSuccess: () => {
            toast.success("Site confirmed. The job can go to a tech.");
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            void qc.invalidateQueries({ queryKey: ["close-out", "work-orders-waiting"] });
            void qc.invalidateQueries({ queryKey: ["dispatch"] });
        },
        onError: () => toast.error("Couldn't save that. Try again."),
    });
    const c = q.data?.check;
    if (!needed || !c) return null;
    return (
        <section
            className="flex flex-col gap-2 rounded-xl border border-amber-400 bg-amber-50 px-3 py-2.5 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
            data-testid="site-check-pick"
            aria-label="Check the site"
        >
            <div className="flex items-center gap-2 font-bold">
                <MapPin className="h-4 w-4" aria-hidden />
                Check the site
                <span className="text-sm font-normal">
                    {c.unmatched
                        ? "We couldn't tell which store this work order is for."
                        : "We weren't sure this is the right store."}{" "}
                    It won&apos;t go to a tech until someone checks.
                </span>
            </div>
            <dl className="grid gap-1 text-sm sm:grid-cols-2">
                <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide opacity-70">Work order says</dt>
                    <dd data-testid="site-check-wo-says">{c.woSays ?? "No address on the work order"}</dd>
                </div>
                <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide opacity-70">This job is at</dt>
                    <dd data-testid="site-check-job-site">{c.jobSite ?? "No site yet"}</dd>
                </div>
            </dl>
            <div className="flex flex-wrap gap-2">
                {canConfirm && (
                    <Button
                        size="sm"
                        onClick={() => confirm.mutate()}
                        disabled={confirm.isPending}
                        data-testid="site-check-confirm"
                        className="bg-amber-600 text-white hover:bg-amber-700"
                    >
                        Site is right
                    </Button>
                )}
                <Button size="sm" variant="outline" asChild>
                    <Link to={siteCheckReviewHref(c)}>Check the work order</Link>
                </Button>
            </div>
        </section>
    );
}
